/**
 * Reload-burst telemetry.
 *
 * Reports when one screen reloads all of its call-card lists many times in a
 * few seconds. In Nov 2026 one long-open CAD site was sending ~47 full list
 * reloads in ~14 s, saturating the database for every customer. A normal
 * screen reloads about once per realtime update, so a burst points at a
 * client-side fault (e.g. duplicate realtime connections), and the report
 * carries the realtime counters needed to tell which.
 *
 * Sent through jserror/log like SessionCheck, so it lands in App Insights
 * traces as "ReloadBurst {...}".
 */

// NOTE: api and JSErrorType are accessed lazily via (window as any), as in
// session-check-telemetry.ts, to avoid import cycles with the legacy layer.

import { getCurrentUsername } from '@/utils/auth';
import { getRealtimeDiagnostics } from '@/services/azure-web-pubsub-service';

export const BURST_WINDOW_MS = 10_000;
export const BURST_THRESHOLD = 8;
/** At most one report per screen per this period; later bursts are counted. */
export const REPORT_COOLDOWN_MS = 5 * 60_000;

let reloads: { at: number; realtime: boolean }[] = [];
let lastReportAt: number | null = null;
let burstsSinceLastReport = 0;
let inBurst = false;

function getPlatform(): string {
  const w = window as any;
  if (w.isIOSApp) return 'ios-app';
  if (w.isAndroidApp) return 'android-app';
  if (w.isWindowsAppSdkApp) return 'windows-app';
  return 'browser';
}

/** Call once per "reload every list" on an in-progress screen. */
export function noteListReload(screen: string, realtime: boolean, now: number = Date.now()): void {
  reloads.push({ at: now, realtime });
  reloads = reloads.filter((r) => now - r.at <= BURST_WINDOW_MS);

  if (reloads.length < BURST_THRESHOLD) {
    inBurst = false;
    return;
  }
  // One burst = one crossing of the threshold, however long it lasts
  if (inBurst) return;
  inBurst = true;
  burstsSinceLastReport++;

  if (lastReportAt !== null && now - lastReportAt < REPORT_COOLDOWN_MS) return;

  const w = window as any;
  if (!w.api || !w.JSErrorType) return;

  const record = {
    username: getCurrentUsername(),
    screen,
    reloadsInWindow: reloads.length,
    realtimeReloads: reloads.filter((r) => r.realtime).length,
    windowMs: BURST_WINDOW_MS,
    burstsSinceLastReport,
    pageUptimeMin: Math.round(performance.now() / 60_000),
    realtime: getRealtimeDiagnostics(),
    visibility: document.visibilityState,
    platform: getPlatform(),
  };
  lastReportAt = now;
  burstsSinceLastReport = 0;
  w.api.insertNewJSError('ReloadBurst ' + JSON.stringify(record), null, 'reload-burst', w.JSErrorType.CAUGHT, null);
}

/** Test hook */
export function resetReloadBurstTelemetry(): void {
  reloads = [];
  lastReportAt = null;
  burstsSinceLastReport = 0;
  inBurst = false;
}
